import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import Modal from '../components/Modal';

// Санитизация HTML — оставляем только безопасные теги
const sanitizeHtml = (html) => {
  if (!html) return '';
  const allowedTags = ['b', 'strong', 'i', 'em', 'u', 's', 'del', 'code', 'pre', 'a', 'br'];
  return html.replace(/<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/gi, (match, tag) => {
    if (allowedTags.includes(tag.toLowerCase())) return match;
    return '';
  });
};

function Dialogs() {
  // Данные вкладок
  const [tabsData, setTabsData] = useState(null);
  const [activeTab, setActiveTab] = useState('groups'); // groups, bot_chats, managers
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Выбранный элемент и сообщения
  const [selectedItem, setSelectedItem] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Для менеджеров — раскрытый менеджер
  const [expandedManager, setExpandedManager] = useState(null);

  // База знаний
  const [showKnowledgeModal, setShowKnowledgeModal] = useState(false);
  const [knowledgeFormData, setKnowledgeFormData] = useState({
    header1: '',
    header2: '',
    text: '',
  });
  const [selectedAnswer, setSelectedAnswer] = useState(null);

  // Polling
  const [lastMessageId, setLastMessageId] = useState(null);
  const messagesContainerRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, []);

  // Загрузка данных вкладок
  const loadTabsData = useCallback(async () => {
    try {
      const data = await api.getCommunicationsTabs();
      setTabsData(data);
    } catch (error) {
      console.error('Error loading tabs data:', error);
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTabsData();
  }, [loadTabsData]);

  // Загрузка сообщений
  const loadMessages = useCallback(async (item, tab) => {
    try {
      setLoadingMessages(true);
      let data;

      if (tab === 'groups') {
        // Объединённые сообщения: бот + группа
        data = await api.getGroupCombinedMessages(
          item.channel_chattype_id,
          item.bot_ucc_id,
          200
        );
        setMessages(data.messages || []);
      } else if (tab === 'bot_chats') {
        // Только диалог с ботом
        data = await api.getDialogHistory(item.id, 200);
        const msgs = (data.messages || []).map(m => ({
          ...m,
          is_bot_dialog: true,
        }));
        setMessages(msgs);
      } else if (tab === 'managers') {
        // Диалог менеджера (через user_companychat_id)
        data = await api.getDialogHistory(item.user_companychat_id, 200);
        setMessages(data.messages || []);
      }

      if (data?.messages?.length > 0) {
        setLastMessageId(data.messages[data.messages.length - 1].id);
      }

      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Ошибка загрузки сообщений');
    } finally {
      setLoadingMessages(false);
    }
  }, [scrollToBottom]);

  // Выбор элемента
  const handleSelectItem = (item) => {
    setSelectedItem(item);
    setLastMessageId(null);
    loadMessages(item, activeTab);
  };

  // Выбор диалога менеджера
  const handleSelectManagerDialog = (dialog) => {
    setSelectedItem(dialog);
    setLastMessageId(null);
    loadMessages(dialog, 'managers');
  };

  // Переключение вкладок
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedItem(null);
    setMessages([]);
    setExpandedManager(null);
    setSearchQuery('');
  };

  // Polling для обновления сообщений
  useEffect(() => {
    if (!selectedItem) return;

    const pollMessages = async () => {
      try {
        let data;
        if (activeTab === 'groups') {
          data = await api.getGroupCombinedMessages(
            selectedItem.channel_chattype_id,
            selectedItem.bot_ucc_id,
            200
          );
        } else if (activeTab === 'bot_chats') {
          data = await api.getDialogHistory(selectedItem.id, 200);
        } else if (activeTab === 'managers') {
          data = await api.getDialogHistory(selectedItem.user_companychat_id, 200);
        }

        if (data?.messages?.length > 0) {
          const latestId = data.messages[data.messages.length - 1].id;
          if (latestId !== lastMessageId) {
            setMessages(prev => {
              const existingIds = new Set(prev.map(m => m.id));
              const newMsgs = (data.messages || []).filter(m => !existingIds.has(m.id));
              if (newMsgs.length > 0) {
                setTimeout(scrollToBottom, 50);
              }
              return [...prev, ...newMsgs];
            });
            setLastMessageId(latestId);
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    };

    const interval = setInterval(pollMessages, 3000);
    return () => clearInterval(interval);
  }, [selectedItem, activeTab, lastMessageId, scrollToBottom]);

  // Периодическое обновление данных вкладок
  useEffect(() => {
    const interval = setInterval(loadTabsData, 10000);
    return () => clearInterval(interval);
  }, [loadTabsData]);

  // Форматирование времени
  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }) + ' ' +
           date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  };

  // Стили для сообщений
  const getMessageStyle = (senderType, isBotDialog) => {
    if (isBotDialog && senderType === 'bot') {
      return { align: 'justify-end', bubble: 'bg-gray-100 text-gray-800', label: 'Telegram Bot', color: 'bg-gray-500' };
    }
    switch (senderType) {
      case 'user':
        return { align: 'justify-start', bubble: 'bg-blue-50 text-blue-900', label: 'Пользователь', color: 'bg-blue-500' };
      case 'bot':
        return { align: 'justify-end', bubble: 'bg-gray-100 text-gray-800', label: 'Бот', color: 'bg-gray-500' };
      case 'manager':
        return { align: 'justify-end', bubble: 'bg-green-50 text-green-900', label: 'Менеджер', color: 'bg-green-500' };
      default:
        return { align: 'justify-start', bubble: 'bg-gray-100 text-gray-800', label: 'Система', color: 'bg-gray-400' };
    }
  };

  // Добавление в базу знаний
  const handleAddToKnowledge = (msg, idx) => {
    let nextBotMsg = null;
    for (let i = idx + 1; i < messages.length; i++) {
      if (messages[i].sender_type === 'bot') {
        nextBotMsg = messages[i];
        break;
      }
    }
    setSelectedAnswer(nextBotMsg);
    setKnowledgeFormData({
      header1: '',
      header2: msg.message || '',
      text: nextBotMsg?.message || '',
    });
    setShowKnowledgeModal(true);
  };

  const handleSelectAnswer = (msg) => {
    setSelectedAnswer(msg);
    setKnowledgeFormData(prev => ({ ...prev, text: msg.message || '' }));
  };

  const handleSaveToKnowledge = async () => {
    if (!knowledgeFormData.header2 || !knowledgeFormData.text) {
      toast.error('Вопрос и ответ обязательны');
      return;
    }
    try {
      await api.createCompanyInfoExample({
        channel_id: 2,
        header1: knowledgeFormData.header1 || null,
        header2: knowledgeFormData.header2,
        text: knowledgeFormData.text,
        is_active: true,
        metadata: null,
      });
      toast.success('Пример добавлен в базу знаний');
      setShowKnowledgeModal(false);
    } catch (error) {
      toast.error(error.message || 'Ошибка при сохранении примера');
    }
  };

  const getBotMessages = () => messages.filter(m => m.sender_type === 'bot');

  // Фильтрация по поиску
  const getFilteredItems = () => {
    if (!tabsData) return [];
    const query = searchQuery.toLowerCase();

    if (activeTab === 'groups') {
      return (tabsData.groups?.items || []).filter(g =>
        !query || g.group_name?.toLowerCase().includes(query) || g.user_name?.toLowerCase().includes(query)
      );
    }
    if (activeTab === 'bot_chats') {
      return (tabsData.bot_chats?.items || []).filter(b =>
        !query || b.user_name?.toLowerCase().includes(query) || b.external_id?.includes(query)
      );
    }
    if (activeTab === 'managers') {
      return (tabsData.managers?.items || []).filter(m =>
        !query || m.name?.toLowerCase().includes(query) || String(m.id_telegram)?.includes(query)
      );
    }
    return [];
  };

  const getManagerStatusStyle = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-yellow-100 text-yellow-800';
      case 'deleted':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getManagerStatusText = (status) => {
    switch (status) {
      case 'active': return 'Активен';
      case 'inactive': return 'Неактивен';
      case 'deleted': return 'Удалён';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="h-[calc(100vh-120px)] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const filteredItems = getFilteredItems();

  return (
    <div className="h-[calc(100vh-72px)] flex bg-white overflow-hidden">
      {/* Левая панель */}
      <div className="w-80 border-r border-gray-200 flex flex-col h-full overflow-hidden">
        {/* Заголовок */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-lg font-semibold text-gray-900">Центр коммуникаций</h1>
            <button
              onClick={loadTabsData}
              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>

          {/* 3 вкладки */}
          <div className="flex space-x-1 bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => handleTabChange('groups')}
              className={`flex-1 px-1.5 py-1.5 text-[10px] font-medium rounded-md transition-colors whitespace-nowrap ${
                activeTab === 'groups'
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Группы
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${
                activeTab === 'groups' ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'
              }`}>
                {tabsData?.groups?.count || 0}
              </span>
            </button>
            <button
              onClick={() => handleTabChange('bot_chats')}
              className={`flex-1 px-1.5 py-1.5 text-[10px] font-medium rounded-md transition-colors whitespace-nowrap ${
                activeTab === 'bot_chats'
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              С ботом
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${
                activeTab === 'bot_chats' ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'
              }`}>
                {tabsData?.bot_chats?.count || 0}
              </span>
            </button>
            <button
              onClick={() => handleTabChange('managers')}
              className={`flex-1 px-1.5 py-1.5 text-[10px] font-medium rounded-md transition-colors whitespace-nowrap ${
                activeTab === 'managers'
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Менеджеры
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${
                activeTab === 'managers' ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'
              }`}>
                {tabsData?.managers?.count || 0}
              </span>
            </button>
          </div>
        </div>

        {/* Поиск */}
        <div className="px-3 py-2 border-b border-gray-100">
          <input
            type="text"
            placeholder="Поиск..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
          />
        </div>

        {/* Список */}
        <div className="flex-1 overflow-y-auto">
          {filteredItems.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
              Нет данных
            </div>
          ) : (
            <>
              {/* ===== ГРУППЫ ===== */}
              {activeTab === 'groups' && filteredItems.map((group) => (
                <div
                  key={`group-${group.channel_chattype_id}`}
                  onClick={() => handleSelectItem(group)}
                  className={`px-3 py-2.5 border-b border-gray-50 cursor-pointer transition-colors ${
                    selectedItem?.channel_chattype_id === group.channel_chattype_id
                      ? 'bg-blue-50 border-l-2 border-l-blue-500'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center space-x-2.5">
                    <div className="w-9 h-9 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                      G
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm text-gray-900 truncate">
                          Иван Везёт {group.group_name?.match(/LS-\d+/)?.[0] || ''}
                        </span>
                        <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                          {formatTime(group.last_message_time)}
                        </span>
                      </div>
                      <div className="mt-0.5">
                        {group.interest ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                            {group.interest}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                            Интерес не определён
                          </span>
                        )}
                      </div>
                      {group.last_message && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {group.last_message.replace(/<[^>]*>/g, '')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* ===== ЧАТЫ С БОТОМ ===== */}
              {activeTab === 'bot_chats' && filteredItems.map((chat) => (
                <div
                  key={`bot-${chat.id}`}
                  onClick={() => handleSelectItem(chat)}
                  className={`px-3 py-2.5 border-b border-gray-50 cursor-pointer transition-colors ${
                    selectedItem?.id === chat.id
                      ? 'bg-blue-50 border-l-2 border-l-blue-500'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center space-x-2.5">
                    <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                      B
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm text-gray-900 truncate">
                          {chat.user_name}
                        </span>
                        <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                          {formatTime(chat.last_message_time)}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        TG: {chat.external_id || 'N/A'}
                      </div>
                      {chat.last_message && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {chat.last_message.replace(/<[^>]*>/g, '')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* ===== МЕНЕДЖЕРЫ ===== */}
              {activeTab === 'managers' && filteredItems.map((manager) => (
                <div key={`manager-${manager.id}`}>
                  <div
                    onClick={() => setExpandedManager(expandedManager === manager.id ? null : manager.id)}
                    className={`px-3 py-2.5 border-b border-gray-100 cursor-pointer transition-colors ${
                      expandedManager === manager.id ? 'bg-gray-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-2.5">
                      <div className="w-9 h-9 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                        M
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm text-gray-900 truncate">
                            {manager.name}
                          </span>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getManagerStatusStyle(manager.status)}`}>
                            {getManagerStatusText(manager.status)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          TG: {manager.id_telegram || 'N/A'}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5 leading-tight">
                          <div>Диалогов: {manager.dialogs_count}</div>
                          <div>Активных: {manager.active_dialogs_count}</div>
                        </div>
                      </div>
                      <svg
                        className={`w-4 h-4 text-gray-400 transition-transform ${expandedManager === manager.id ? 'rotate-180' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>

                  {/* Диалоги менеджера */}
                  {expandedManager === manager.id && (
                    <div className="bg-gray-50">
                      {manager.dialogs.length === 0 ? (
                        <div className="px-4 py-3 text-xs text-gray-400 text-center">
                          Нет диалогов
                        </div>
                      ) : (
                        manager.dialogs.map((dialog) => (
                          <div
                            key={`dialog-${dialog.dialog_id}`}
                            onClick={() => handleSelectManagerDialog(dialog)}
                            className={`px-4 py-2 pl-14 border-b border-gray-100 cursor-pointer transition-colors ${
                              selectedItem?.dialog_id === dialog.dialog_id
                                ? 'bg-blue-50 border-l-2 border-l-blue-500'
                                : 'hover:bg-gray-100'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-800 truncate">
                                {dialog.user_name}
                              </span>
                              <span className={`text-xs px-1.5 py-0.5 rounded ${
                                dialog.status === 'active' ? 'bg-green-100 text-green-700' :
                                dialog.status === 'completed' ? 'bg-gray-100 text-gray-600' :
                                dialog.status === 'closed' ? 'bg-gray-100 text-gray-600' :
                                dialog.status === 'closed_by_inactivity' ? 'bg-orange-100 text-orange-700' :
                                (dialog.status === 'pending_manager' || dialog.status === 'awaiting_manager') ? 'bg-blue-100 text-blue-700' :
                                dialog.status === 'declined' ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {dialog.status === 'active' ? 'Активен' :
                                 dialog.status === 'completed' ? 'Завершён' :
                                 dialog.status === 'closed' ? 'Закрыт' :
                                 dialog.status === 'closed_by_inactivity' ? 'Закрыт (неактивность)' :
                                 (dialog.status === 'pending_manager' || dialog.status === 'awaiting_manager') ? 'Ожидает менеджера' :
                                 dialog.status === 'declined' ? 'Отклонён' :
                                 dialog.status}
                              </span>
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">
                              {formatTime(dialog.last_activity_at || dialog.start_time)}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Правая панель - чат */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {!selectedItem ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <div className="text-6xl mb-4">💬</div>
              <p className="text-lg font-medium">Выберите чат для просмотра сообщений</p>
            </div>
          </div>
        ) : (
          <>
            {/* Шапка чата */}
            <div className="bg-white border-b border-gray-200 px-4 py-3 flex-shrink-0">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${
                  activeTab === 'groups' ? 'bg-gradient-to-br from-purple-500 to-indigo-600' :
                  activeTab === 'bot_chats' ? 'bg-gradient-to-br from-blue-500 to-cyan-500' :
                  'bg-gradient-to-br from-green-500 to-emerald-600'
                }`}>
                  {activeTab === 'groups' ? 'G' : activeTab === 'bot_chats' ? 'B' : 'M'}
                </div>
                <div>
                  <h2 className="font-bold text-gray-900">
                    {activeTab === 'groups'
                      ? `Иван Везёт ${selectedItem.group_name?.match(/LS-\d+/)?.[0] || ''}`
                      : activeTab === 'bot_chats'
                        ? selectedItem.user_name
                        : selectedItem.user_name
                    }
                  </h2>
                  <p className="text-xs text-gray-500">
                    {activeTab === 'groups' && selectedItem.interest
                      ? `Интерес: ${selectedItem.interest}`
                      : activeTab === 'bot_chats'
                        ? `TG: ${selectedItem.external_id || 'N/A'}`
                        : `Диалог менеджера`
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Сообщения */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3" ref={messagesContainerRef}>
              {loadingMessages ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : messages.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-400">
                  Нет сообщений
                </div>
              ) : (
                <>
                  {messages.map((msg, idx) => {
                    const msgText = msg.message || '';
                    if (!msgText.trim() && !msg.media_files) return null;

                    const style = getMessageStyle(msg.sender_type, msg.is_bot_dialog);
                    const prevMsg = idx > 0 ? messages[idx - 1] : null;
                    const showBotDialogSeparator = activeTab === 'groups' &&
                      prevMsg?.is_bot_dialog && !msg.is_bot_dialog;

                    return (
                      <React.Fragment key={msg.id || idx}>
                        {showBotDialogSeparator && (
                          <div className="flex items-center my-4">
                            <div className="flex-1 border-t border-gray-300"></div>
                            <span className="px-3 text-xs text-gray-500 font-medium">Диалог в группе</span>
                            <div className="flex-1 border-t border-gray-300"></div>
                          </div>
                        )}
                        {activeTab === 'groups' && idx === 0 && msg.is_bot_dialog && (
                          <div className="flex items-center mb-3">
                            <div className="flex-1 border-t border-blue-200"></div>
                            <span className="px-3 text-xs text-blue-500 font-medium">💭 Диалог с ботом</span>
                            <div className="flex-1 border-t border-blue-200"></div>
                          </div>
                        )}
                        <div className={`flex ${style.align} group`}>
                          <div className={`max-w-[70%] rounded-xl px-3 py-2 ${style.bubble}`}>
                            <div className="flex items-center space-x-1.5 mb-1">
                              <div className={`w-5 h-5 rounded-full ${style.color} flex items-center justify-center text-white text-xs`}>
                                {(msg.sender_name || style.label).charAt(0).toUpperCase()}
                              </div>
                              <span className="text-xs font-medium opacity-75">
                                {msg.sender_name || style.label}
                              </span>
                              {msg.sender_type === 'user' && (
                                <button
                                  onClick={() => handleAddToKnowledge(msg, idx)}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-green-600 hover:bg-green-50 rounded"
                                  title="Добавить в базу знаний"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                  </svg>
                                </button>
                              )}
                            </div>
                            <p className="text-sm whitespace-pre-wrap break-words" dangerouslySetInnerHTML={{ __html: sanitizeHtml(msgText) }} />
                            <div className="text-xs opacity-50 mt-1 text-right">
                              {formatTime(msg.created_at)}
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Модалка для добавления в базу знаний */}
      {showKnowledgeModal && (
        <Modal
          isOpen={showKnowledgeModal}
          onClose={() => setShowKnowledgeModal(false)}
          title="Добавить в базу знаний"
        >
          <div className="space-y-4">
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">Категория</label>
              <input
                type="text"
                value={knowledgeFormData.header1}
                onChange={(e) => setKnowledgeFormData(prev => ({ ...prev, header1: e.target.value }))}
                placeholder="Введите категорию (необязательно)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">Вопрос *</label>
              <textarea
                value={knowledgeFormData.header2}
                onChange={(e) => setKnowledgeFormData(prev => ({ ...prev, header2: e.target.value }))}
                placeholder="Вопрос пользователя"
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">Ответ *</label>
              <textarea
                value={knowledgeFormData.text}
                onChange={(e) => setKnowledgeFormData(prev => ({ ...prev, text: e.target.value }))}
                placeholder="Ответ бота"
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Выбрать другой ответ из диалога</label>
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-2">
                {getBotMessages().map((botMsg, i) => (
                  <div
                    key={botMsg.id || i}
                    onClick={() => handleSelectAnswer(botMsg)}
                    className={`p-2 rounded cursor-pointer transition-colors ${
                      selectedAnswer?.id === botMsg.id
                        ? 'bg-blue-100 border border-blue-400'
                        : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    <div className="text-xs text-gray-500 mb-1">
                      {formatTime(botMsg.created_at)}
                    </div>
                    <p className="text-sm text-gray-900 line-clamp-2">
                      {botMsg.message}
                    </p>
                  </div>
                ))}
                {getBotMessages().length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">
                    В диалоге нет сообщений бота
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4 pb-4 mt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowKnowledgeModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={handleSaveToKnowledge}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
              >
                Сохранить
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default Dialogs;
